import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Power,
  X,
  Loader2,
  ShieldCheck,
  ChevronDown,
  AlertCircle,
  Landmark,
  Wallet,
  TrendingUp,
  Layers,
} from "lucide-react";
import {
  fetchPFRules,
  createPFRule,
  updatePFRule,
  deletePFRule,
  togglePFRuleStatus,
  fetchSalaryComponents,
} from "../../services/pfRulesApi";
import PFInterest from "./PFInterest";
import PFInterestCalculation from "./PFInterestCalculation";
import { useAuth } from "../../contexts/AuthContext";

/* ============================================================
   CONSTANTS
   ============================================================ */

const ALL_PF_TYPES = ["PFS", "VPF", "PFADV", "CPF"];
const ACTIVE_PF_TYPES = ["PFS", "CPF"];
const PF_TYPES = ALL_PF_TYPES;
const PENSION_SCHEME_TYPES = ["GPF", "NPS"];
const CALC_TYPES = ["PERCENTAGE", "FIXED"];
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
const YEARS = Array.from({ length: 8 }, (_, i) => 2023 + i);

const PF_TYPE_META = {
  PFS: { label: "PFS", color: "#0F766E", bg: "#ECFDF5", icon: Landmark },
  VPF: { label: "VPF", color: "#B45309", bg: "#FFFBEB", icon: TrendingUp },
  PFADV: { label: "PFADV", color: "#6D28D9", bg: "#F5F3FF", icon: Wallet },
  CPF: { label: "CPF", color: "#1D4ED8", bg: "#EFF6FF", icon: Layers },
};

const emptyForm = {
  ruleName: "",
  pfType: "",
  pensionSchemeType: "",
  calcType: "PERCENTAGE",
  percentValue: "",
  employeePercentage: "",
  employerPercentage: "",
  amountValue: "",
  calcBase: [],
  effFromMonth: "",
  effFromYear: "",
  effToMonth: "",
  effToYear: "",
  status: "ACTIVE",
};


/* ============================================================
   SMALL UI PRIMITIVES
   ============================================================ */

function StatusBadge({ status }) {
  const isActive = status === "ACTIVE";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${
        isActive
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
          : "bg-slate-100 text-slate-600 ring-slate-200"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          isActive ? "bg-emerald-500" : "bg-slate-400"
        }`}
      />
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

function PfTypeTag({ type }) {
  const meta = PF_TYPE_META[type] || {
    label: type,
    color: "#334155",
    bg: "#F1F5F9",
    icon: Landmark,
  };
  const Icon = meta.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold"
      style={{ color: meta.color, backgroundColor: meta.bg }}
    >
      <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
      {meta.label}
    </span>
  );
}

function Field({ label, required, error, children, className = "" }) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="ml-0.5 text-rose-500">*</span>}
      </label>
      {children}
      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-xs font-medium text-rose-600">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}

const inputBase =
  "w-full rounded-lg border bg-white px-3 py-2 text-sm text-slate-800 shadow-sm outline-none transition focus:ring-2 focus:ring-offset-0";
const inputOk = "border-slate-300 focus:border-blue-500 focus:ring-blue-100";
const inputErr = "border-rose-400 focus:border-rose-500 focus:ring-rose-100";

/* ============================================================
   CREATE / EDIT MODAL
   ============================================================ */

function RuleFormModal({
  open,
  onClose,
  onSubmit,
  initialData,
  isEdit,
  saving,
  salaryComponents,
}) {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(initialData || emptyForm);
      setErrors({});
      setTimeout(() => firstFieldRef.current?.focus(), 50);
    }
  }, [open, initialData]);

  if (!open) return null;

  const setField = (key, val) => {
    setForm((f) => {
      const updated = {
        ...f,
        [key]: val,
      };

      if (key === "calcType") {
        if (val === "FIXED") {
          updated.calcBase = [];
          updated.percentValue = "";
        }

        if (val === "PERCENTAGE") {
          updated.amountValue = "";
        }
      }

      return updated;
    });

    setErrors((e) => ({
      ...e,
      [key]: undefined,
      calcBase: undefined,
    }));
  };

  const toggleBase = (opt) => {
    setForm((f) => {
      const has = f.calcBase.includes(opt);
      return {
        ...f,
        calcBase: has
          ? f.calcBase.filter((o) => o !== opt)
          : [...f.calcBase, opt],
      };
    });
    setErrors((e) => ({ ...e, calcBase: undefined }));
  };

  const formPfTypes = ACTIVE_PF_TYPES.includes(form.pfType) || !form.pfType
    ? ACTIVE_PF_TYPES
    : [form.pfType, ...ACTIVE_PF_TYPES];

  const validate = () => {
    const errs = {};
    if (!form.ruleName.trim()) errs.ruleName = "Rule name is required";
    if (!form.pfType) errs.pfType = "Select a PF type";
    if (!form.pensionSchemeType) errs.pensionSchemeType = "Please select Pension Scheme Type.";

    if (form.pfType === "CPF") {
      if (!form.employeePercentage) {
        errs.employeePercentage = "Employee contribution percentage is required";
      } else if (
        Number(form.employeePercentage) < 0 ||
        Number(form.employeePercentage) > 100
      ) {
        errs.employeePercentage = "Enter a value between 0 and 100";
      }

      if (!form.employerPercentage) {
        errs.employerPercentage = "Employer contribution percentage is required";
      } else if (
        Number(form.employerPercentage) < 0 ||
        Number(form.employerPercentage) > 100
      ) {
        errs.employerPercentage = "Enter a value between 0 and 100";
      }
    } else {
      if (!form.percentValue) {
        errs.percentValue = "Enter a percentage";
      } else if (
        Number(form.percentValue) < 0 ||
        Number(form.percentValue) > 100
      ) {
        errs.percentValue = "Enter a value between 0 and 100";
      }
    }

    if (!form.calcBase || form.calcBase.length === 0) {
      errs.calcBase = "Select at least one calculation base";
    }

    if (!form.effFromMonth || !form.effFromYear)
      errs.effFrom = "Effective from is required";
    if (
      (form.effToMonth && !form.effToYear) ||
      (!form.effToMonth && form.effToYear)
    )
      errs.effTo = "Select both month and year, or leave both empty";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    onSubmit(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      <div className="relative z-10 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 animate-[fadeIn_.15s_ease-out]">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white/95 px-6 py-4 backdrop-blur">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              {isEdit ? "Edit PF Rule" : "Create PF Rule"}
            </h2>
            <p className="text-xs text-slate-500">
              Define how this rule calculates Provident Fund contributions.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 px-6 py-5">
          <Field label="Rule Name" required error={errors.ruleName}>
            <input
              ref={firstFieldRef}
              type="text"
              value={form.ruleName}
              onChange={(e) => setField("ruleName", e.target.value)}
              placeholder="e.g. Government CPF"
              className={`${inputBase} ${errors.ruleName ? inputErr : inputOk}`}
            />
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="PF Type" required error={errors.pfType}>
              <div className="relative">
                <select
                  value={form.pfType}
                  onChange={(e) => setField("pfType", e.target.value)}
                  className={`${inputBase} appearance-none pr-9 ${errors.pfType ? inputErr : inputOk}`}
                >
                  <option value="">Select PF type</option>
                  {formPfTypes.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-2.5 h-4 w-4 text-slate-400" />
              </div>
            </Field>

            <Field label="Pension Scheme Type" required error={errors.pensionSchemeType}>
              <div className="relative">
                <select
                  value={form.pensionSchemeType}
                  onChange={(e) => setField("pensionSchemeType", e.target.value)}
                  className={`${inputBase} appearance-none pr-9 ${errors.pensionSchemeType ? inputErr : inputOk}`}
                >
                  <option value="">Select pension scheme type</option>
                  {PENSION_SCHEME_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-2.5 h-4 w-4 text-slate-400" />
              </div>
            </Field>
          </div>

          {form.pfType === "CPF" ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field
                label="Employee Contribution Percentage (%)"
                required
                error={errors.employeePercentage}
              >
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.employeePercentage}
                    onChange={(e) => setField("employeePercentage", e.target.value)}
                    placeholder="e.g. 10"
                    className={`${inputBase} pr-8 ${errors.employeePercentage ? inputErr : inputOk}`}
                  />
                  <span className="absolute right-3 top-2 text-sm text-slate-400">
                    %
                  </span>
                </div>
              </Field>

              <Field
                label="Employer Contribution Percentage (%)"
                required
                error={errors.employerPercentage}
              >
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.employerPercentage}
                    onChange={(e) => setField("employerPercentage", e.target.value)}
                    placeholder="e.g. 14"
                    className={`${inputBase} pr-8 ${errors.employerPercentage ? inputErr : inputOk}`}
                  />
                  <span className="absolute right-3 top-2 text-sm text-slate-400">
                    %
                  </span>
                </div>
              </Field>
            </div>
          ) : (
            <Field
              label="Percentage %"
              required
              error={errors.percentValue}
              className="sm:w-1/2"
            >
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.percentValue}
                  onChange={(e) => setField("percentValue", e.target.value)}
                  placeholder="e.g. 8.33"
                  className={`${inputBase} pr-8 ${errors.percentValue ? inputErr : inputOk}`}
                />
                <span className="absolute right-3 top-2 text-sm text-slate-400">
                  %
                </span>
              </div>
            </Field>
          )}

          <Field label="Calculation Base" required error={errors.calcBase}>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              {salaryComponents.map((component) => {
                const checked = form.calcBase.includes(component.id);
                return (
                  <label
                    key={component.id}
                    className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
                      checked
                        ? "border-blue-400 bg-blue-50 text-blue-800"
                        : "border-slate-200 text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleBase(component.id)}
                      className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    {component.component_name}
                  </label>
                );
              })}
            </div>
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Effective From" required error={errors.effFrom}>
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={form.effFromMonth}
                  onChange={(e) => setField("effFromMonth", e.target.value)}
                  className={`${inputBase} ${errors.effFrom ? inputErr : inputOk}`}
                >
                  <option value="">Month</option>
                  {MONTHS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <select
                  value={form.effFromYear}
                  onChange={(e) => setField("effFromYear", e.target.value)}
                  className={`${inputBase} ${errors.effFrom ? inputErr : inputOk}`}
                >
                  <option value="">Year</option>
                  {YEARS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
            </Field>

            <Field label="Effective To" error={errors.effTo}>
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={form.effToMonth}
                  onChange={(e) => setField("effToMonth", e.target.value)}
                  className={`${inputBase} ${errors.effTo ? inputErr : inputOk}`}
                >
                  <option value="">Month</option>
                  {MONTHS.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
                <select
                  value={form.effToYear}
                  onChange={(e) => setField("effToYear", e.target.value)}
                  className={`${inputBase} ${errors.effTo ? inputErr : inputOk}`}
                >
                  <option value="">Year</option>
                  {YEARS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <p className="mt-1 text-xs text-slate-400">
                Leave blank if the rule has no end date.
              </p>
            </Field>
          </div>

          <Field label="Status">
            <div className="flex gap-2">
              {["ACTIVE", "INACTIVE"].map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => setField("status", s)}
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                    form.status === s
                      ? s === "ACTIVE"
                        ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                        : "border-slate-400 bg-slate-100 text-slate-700"
                      : "border-slate-200 text-slate-500 hover:bg-slate-50"
                  }`}
                >
                  {s === "ACTIVE" ? "Active" : "Inactive"}
                </button>
              ))}
            </div>
          </Field>

          <div className="flex items-center justify-end gap-3 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Rule
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ============================================================
   DELETE CONFIRM DIALOG
   ============================================================ */

function ConfirmDeleteModal({ open, ruleName, onCancel, onConfirm, deleting }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onCancel}
      />
      <div className="relative z-10 w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-slate-200">
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-rose-50">
          <Trash2 className="h-5 w-5 text-rose-600" />
        </div>
        <h3 className="text-base font-semibold text-slate-900">
          Delete this rule?
        </h3>
        <p className="mt-1.5 text-sm text-slate-500">
          <span className="font-medium text-slate-700">{ruleName}</span> will be
          permanently removed from PF configuration. This cannot be undone.
        </p>
        <div className="mt-5 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:opacity-60"
          >
            {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
            Delete Rule
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */

export default function ProvidentFund() {
  const { hasPermission } = useAuth();
  const canView = hasPermission('pf:view');
  const canAdd = hasPermission('pf:add');

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [pfTypeFilter, setPfTypeFilter] = useState("");
  const [pensionSchemeFilter, setPensionSchemeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [salaryComponents, setSalaryComponents] = useState([]);
  const [loadingComponents, setLoadingComponents] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [activeTab, setActiveTab] = useState("configuration");
  const [toast, setToast] = useState(null);

  const showToast = (message, tone = "success") => {
    setToast({ message, tone });
    setTimeout(() => setToast(null), 2600);
  };

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPFRules(null, null, pfTypeFilter || null, pensionSchemeFilter || null);
      setRules(data.rules || []);
    } catch (error) {
      showToast(error.message || "Failed to load PF rules", "error");
    } finally {
      setLoading(false);
    }
  }, [pfTypeFilter, pensionSchemeFilter]);

  const fetchSalaryComponentsData = useCallback(async () => {
    setLoadingComponents(true);
    try {
      const data = await fetchSalaryComponents();
      setSalaryComponents(data.components || []);
    } catch (error) {
      console.error("Failed to load salary components:", error);
    } finally {
      setLoadingComponents(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
    fetchSalaryComponentsData();
  }, [fetchRules, fetchSalaryComponentsData]);

  const filteredRules = useMemo(() => {
    return rules.filter((r) => {
      const matchesSearch = r.rule_name
        .toLowerCase()
        .includes(search.trim().toLowerCase()) ||
        r.pension_scheme_type?.toLowerCase().includes(search.trim().toLowerCase());
      const matchesType = pfTypeFilter ? r.pf_type === pfTypeFilter : true;
      const matchesPensionScheme = pensionSchemeFilter ? r.pension_scheme_type === pensionSchemeFilter : true;
      const matchesStatus = statusFilter 
        ? (r.status || "ACTIVE").toUpperCase() === statusFilter.toUpperCase() 
        : true;
      return matchesSearch && matchesType && matchesPensionScheme && matchesStatus;
    });
  }, [rules, search, pfTypeFilter, pensionSchemeFilter, statusFilter]);

  /* ---------- form <-> record mapping ---------- */

  const recordToForm = (r) => {
    const monthNumbers = { 1: "Jan", 2: "Feb", 3: "Mar", 4: "Apr", 5: "May", 6: "Jun", 7: "Jul", 8: "Aug", 9: "Sep", 10: "Oct", 11: "Nov", 12: "Dec" };
    
    const empPct = r.employee_percentage !== null && r.employee_percentage !== undefined
      ? String(r.employee_percentage)
      : (r.percentage !== null && r.percentage !== undefined ? String(r.percentage) : "");
    const emplyrPct = r.employer_percentage !== null && r.employer_percentage !== undefined
      ? String(r.employer_percentage)
      : "";

    return {
      ruleName: r.rule_name || "",
      pfType: r.pf_type || "",
      pensionSchemeType: r.pension_scheme_type || "",
      calcType: r.calculation_type || "PERCENTAGE",
      percentValue: empPct,
      employeePercentage: empPct,
      employerPercentage: emplyrPct,
      amountValue: r.fixed_amount !== null && r.fixed_amount !== undefined ? String(r.fixed_amount) : "",
      calcBase: r.salary_component_ids || [],
      effFromMonth: monthNumbers[r.effective_from_month] || "",
      effFromYear: String(r.effective_from_year || ""),
      effToMonth: r.effective_to_month ? monthNumbers[r.effective_to_month] : "",
      effToYear: r.effective_to_year ? String(r.effective_to_year) : "",
      status: r.status || "ACTIVE",
    };
  };

  const formToPayload = (form) => {
    const monthNames = { "Jan": 1, "Feb": 2, "Mar": 3, "Apr": 4, "May": 5, "Jun": 6, "Jul": 7, "Aug": 8, "Sep": 9, "Oct": 10, "Nov": 11, "Dec": 12 };
    const isCpf = form.pfType === "CPF";
    const empPct = isCpf
      ? (form.employeePercentage ? parseFloat(form.employeePercentage) : null)
      : (form.percentValue ? parseFloat(form.percentValue) : null);
    const emplyrPct = isCpf && form.employerPercentage ? parseFloat(form.employerPercentage) : null;

    return {
      rule_name: form.ruleName.trim(),
      pf_type: form.pfType,
      pension_scheme_type: form.pensionSchemeType,
      calculation_type: "PERCENTAGE",
      percentage: empPct,
      employee_percentage: empPct,
      employer_percentage: emplyrPct,
      fixed_amount: null,
      salary_component_ids: form.calcBase || [],
      effective_from_month: monthNames[form.effFromMonth],
      effective_from_year: parseInt(form.effFromYear),
      effective_to_month: form.effToMonth ? monthNames[form.effToMonth] : null,
      effective_to_year: form.effToYear ? parseInt(form.effToYear) : null,
      status: form.status,
    };
  };

  /* ---------- handlers ---------- */

  const openCreate = () => {
    setEditMode(false);
    setEditingId(null);
    setDialogOpen(true);
  };

  const openEdit = (rule) => {
    setEditMode(true);
    setEditingId(rule.id);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const handleSubmitForm = async (form) => {
    setSaving(true);
    const payload = formToPayload(form);
    try {
      if (editMode) {
        const updated = await updatePFRule(editingId, payload);
        setRules((prev) => prev.map((r) => (r.id === editingId ? updated : r)));
        showToast("PF rule updated successfully");
      } else {
        const created = await createPFRule(payload);
        setRules((prev) => [created, ...prev]);
        showToast("PF rule created successfully");
      }
      setDialogOpen(false);
    } catch (error) {
      showToast(error.message || "Something went wrong. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (rule) => {
    setTogglingId(rule.id);
    const newStatus = rule.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      const updated = await togglePFRuleStatus(rule.id, newStatus);
      setRules((prev) => prev.map((r) => (r.id === rule.id ? updated : r)));
      showToast(`Rule marked as ${newStatus}`);
    } catch (error) {
      showToast(error.message || "Could not update status", "error");
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deletePFRule(deleteTarget.id);
      setRules((prev) => prev.filter((r) => r.id !== deleteTarget.id));
      showToast("PF rule deleted");
      setDeleteTarget(null);
    } catch (error) {
      showToast(error.message || "Could not delete rule", "error");
    } finally {
      setDeleting(false);
    }
  };

  const activeCount = rules.filter((r) => r.status === "ACTIVE").length;

  /* ---------- render ---------- */

  if (!canView) {
    return (
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-500">
          You do not have permission to view Provident Fund configurations.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-700 shadow-sm">
              <ShieldCheck className="h-5.5 w-5.5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                Provident Fund
              </h1>
              <p className="mt-0.5 text-sm text-slate-500">
                Manage PF rules, investments and annual interest
              </p>
            </div>
          </div>
          {activeTab === "configuration" && (
            <button
              onClick={openCreate}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-800 active:scale-[0.98]"
            >
              <Plus className="h-4 w-4" />
              Create Rule
            </button>
          )}
        </div>

        {/* Sub-tabs ribbon */}
        <div className="mb-6 border-b border-slate-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab("configuration")}
              className={`border-b-2 py-4 px-1 text-sm font-semibold transition-all ${
                activeTab === "configuration"
                  ? "border-blue-700 text-blue-700"
                  : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700"
              }`}
            >
              PF Configuration
            </button>
            <button
              onClick={() => setActiveTab("interest")}
              className={`border-b-2 py-4 px-1 text-sm font-semibold transition-all ${
                activeTab === "interest"
                  ? "border-blue-700 text-blue-700"
                  : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700"
              }`}
            >
              PF Interest Management
            </button>
            <button
              onClick={() => setActiveTab("calculation")}
              className={`border-b-2 py-4 px-1 text-sm font-semibold transition-all ${
                activeTab === "calculation"
                  ? "border-blue-700 text-blue-700"
                  : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700"
              }`}
            >
              PF Interest Calculation
            </button>
          </nav>
        </div>

        {activeTab === "configuration" ? (
          <>
            {/* Summary strip */}
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <p className="text-xs font-medium text-slate-500">Total Rules</p>
            <p className="mt-1 text-lg font-bold text-slate-900">
              {rules.length}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <p className="text-xs font-medium text-slate-500">Active</p>
            <p className="mt-1 text-lg font-bold text-emerald-600">
              {activeCount}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <p className="text-xs font-medium text-slate-500">Inactive</p>
            <p className="mt-1 text-lg font-bold text-slate-500">
              {rules.length - activeCount}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
            <p className="text-xs font-medium text-slate-500">PF Types</p>
            <p className="mt-1 text-lg font-bold text-slate-900">
              {2}
            </p>
          </div>
        </div>

        {/* Search + filters */}
        <div className="mb-4 flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by rule name..."
              className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <select
                value={pfTypeFilter}
                onChange={(e) => setPfTypeFilter(e.target.value)}
                className="appearance-none rounded-lg border border-slate-200 bg-slate-50 py-2 pl-3 pr-8 text-sm outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
              >
                <option value="">All PF Types</option>
                {PF_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-2.5 h-4 w-4 text-slate-400" />
            </div>
            <div className="relative">
              <select
                value={pensionSchemeFilter}
                onChange={(e) => setPensionSchemeFilter(e.target.value)}
                className="appearance-none rounded-lg border border-slate-200 bg-slate-50 py-2 pl-3 pr-8 text-sm outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
              >
                <option value="">All Pension Schemes</option>
                {PENSION_SCHEME_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-2.5 h-4 w-4 text-slate-400" />
            </div>
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="appearance-none rounded-lg border border-slate-200 bg-slate-50 py-2 pl-3 pr-8 text-sm outline-none transition focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100"
              >
                <option value="">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-2.5 h-4 w-4 text-slate-400" />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">Rule Name</th>
                  <th className="px-4 py-3">PF Type</th>
                  <th className="px-4 py-3">Pension Scheme</th>
                  <th className="px-4 py-3">Calculation Type</th>
                  <th className="px-4 py-3">Value</th>
                  <th className="px-4 py-3">Calculation Base</th>
                  <th className="px-4 py-3">Effective From</th>
                  <th className="px-4 py-3">Effective To</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading && (
                  <tr>
                    <td colSpan={10} className="px-4 py-14">
                      <div className="flex flex-col items-center justify-center gap-2 text-slate-400">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span className="text-sm">Loading PF rules…</span>
                      </div>
                    </td>
                  </tr>
                )}

                {!loading && filteredRules.length === 0 && (
                  <tr>
                    <td colSpan={10} className="px-4 py-14">
                      <div className="flex flex-col items-center justify-center gap-1 text-center">
                        <p className="text-sm font-medium text-slate-600">
                          No PF rules found
                        </p>
                        <p className="text-xs text-slate-400">
                          Try adjusting your search or filters, or create a new
                          rule.
                        </p>
                      </div>
                    </td>
                  </tr>
                )}

                {!loading &&
                  filteredRules.map((rule) => (
                    <tr
                      key={rule.id}
                      className="transition hover:bg-slate-50/80"
                    >
                      <td className="px-4 py-3.5 font-medium text-slate-800">
                        {rule.rule_name}
                      </td>
                      <td className="px-4 py-3.5">
                        <PfTypeTag type={rule.pf_type} />
                      </td>
                      <td className="px-4 py-3.5 text-slate-600">
                        {rule.pension_scheme_type || "—"}
                      </td>
                      <td className="px-4 py-3.5 text-slate-600">
                        {rule.calculation_type === "PERCENTAGE" ? "Percentage" : "Fixed Amount"}
                      </td>
                      <td className="px-4 py-3.5 font-semibold text-slate-800">
                        {rule.pf_type === "CPF" ? (
                          <div className="flex flex-col text-xs space-y-0.5">
                            <span><span className="text-slate-500 font-normal">Emp: </span>{rule.employee_percentage ?? rule.percentage}%</span>
                            <span><span className="text-slate-500 font-normal">Employer: </span>{rule.employer_percentage != null ? `${rule.employer_percentage}%` : "—"}</span>
                          </div>
                        ) : rule.calculation_type === "PERCENTAGE" ? (
                          `${rule.percentage}%`
                        ) : (
                          `₹${Number(rule.fixed_amount).toLocaleString("en-IN")}`
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-slate-600">
                        <div className="flex flex-wrap gap-1">
                          {rule.salary_component_ids && rule.salary_component_ids.length > 0 ? (
                            rule.salary_component_ids.map((id) => {
                              const comp = salaryComponents.find(c => c.id === id);
                              return comp ? (
                                <span
                                  key={id}
                                  className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600"
                                >
                                  {comp.component_name}
                                </span>
                              ) : null;
                            })
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-slate-600">
                        {rule.effective_from_month}/{rule.effective_from_year}
                      </td>
                      <td className="px-4 py-3.5 text-slate-500">
                        {rule.effective_to_month && rule.effective_to_year 
                          ? `${rule.effective_to_month}/${rule.effective_to_year}` 
                          : "—"}
                      </td>
                      <td className="px-4 py-3.5">
                        <StatusBadge status={rule.status} />
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          {canAdd && (
                            <>
                              <button
                                onClick={() => openEdit(rule)}
                                title="Edit"
                                className="rounded-md p-1.5 text-slate-500 transition hover:bg-blue-50 hover:text-blue-700"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleToggleStatus(rule)}
                                title={
                                  rule.status === "ACTIVE"
                                    ? "Deactivate"
                                    : "Activate"
                                }
                                disabled={togglingId === rule.id}
                                className="rounded-md p-1.5 text-slate-500 transition hover:bg-amber-50 hover:text-amber-700 disabled:opacity-50"
                              >
                                {togglingId === rule.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Power className="h-4 w-4" />
                                )}
                              </button>
                              <button
                                onClick={() => setDeleteTarget(rule)}
                                title="Delete"
                                className="rounded-md p-1.5 text-slate-500 transition hover:bg-rose-50 hover:text-rose-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-3 text-xs text-slate-400">
          Showing {filteredRules.length} of {rules.length} rule
          {rules.length === 1 ? "" : "s"}
        </p>
      </>
    ) : activeTab === "interest" ? (
      <PFInterest isEmbedded={true} />
    ) : (
      <PFInterestCalculation />
    )}
  </div>

      {/* Modals */}
      <RuleFormModal
        open={dialogOpen}
        onClose={closeDialog}
        onSubmit={handleSubmitForm}
        isEdit={editMode}
        saving={saving}
        initialData={
          editMode
            ? recordToForm(rules.find((r) => r.id === editingId) || {})
            : emptyForm
        }
        salaryComponents={salaryComponents}
      />

      <ConfirmDeleteModal
        open={!!deleteTarget}
        ruleName={deleteTarget?.ruleName}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        deleting={deleting}
      />

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg transition ${
            toast.tone === "error" ? "bg-rose-600" : "bg-slate-900"
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
